import React, { useCallback, useEffect, useMemo, useState } from "react";
import Stack from "@mui/material/Stack";
import {
  DataGridPro as MuiDataGrid,
  GridActionsCellItem,
  GridToolbarQuickFilter,
  GridToolbarExport,
} from "@mui/x-data-grid-pro";
import { Typography } from "@mui/material";

import { useSessionStore } from "../../services/hooks";

const DataGrid = ({
  actions = [],
  checkboxSelection = false,
  columns = [],
  datagridKey,
  disableSelectAllCheckbox = false,
  error,
  filterMode = "client",
  filterModel: initialFilterModel,
  loading,
  noRowsMessage = "No results matched your search criteria.",
  onFilterModelChange: handleFilterModelChange = () => {},
  onPaginationModelChange: handlePaginationModelChange = () => {},
  onQueryParameterChange: handleQueryParameterChange,
  onRowSelectionModelChange: handleSelectionModelChange,
  onSortModelChange: handleSortModelChange = () => {},
  paginationMode = "client",
  paginationModel: initialPaginationModel,
  rowCount,
  rows = [],
  sortingMode = "client",
  sortModel: initialSortModel,
  sx = {},
  ...props
}) => {
  const [datagridModels, datagridStore] = useSessionStore("datagrid");

  const [filterModel, setFilterModel] = useState(
    () => datagridStore.get(datagridKey)?.filterModel || initialFilterModel,
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    () => datagridStore.get(datagridKey)?.columnVisibilityModel || {},
  );
  const [paginationModel, setPaginationModel] = useState(
    initialPaginationModel,
  );
  const [sortModel, setSortModel] = useState(
    () => datagridStore.get(datagridKey)?.sortModel || initialSortModel,
  );
  useEffect(() => {
    if (!datagridKey || !datagridModels || datagridModels[datagridKey]) return;
    datagridStore.init(datagridKey)({
      filterModel: initialFilterModel,
      sortModel: initialSortModel,
    });
  });

  useEffect(() => {
    if (!datagridKey || !datagridModels) return;
    const models = datagridModels[datagridKey];
    models?.sortModel && setSortModel(models.sortModel);
  }, [datagridKey, datagridModels]);

  const onFilterModelChange = useCallback(
    (model) => {
      if (filterMode === "server" && loading) return;
      if (datagridKey) {
        datagridStore.set(datagridKey)({ filterModel: model });
        if (filterMode === "server")
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
      setFilterModel(model);
      handleFilterModelChange(model);
    },
    [datagridKey, datagridStore, filterMode, handleFilterModelChange, loading],
  );

  const onPaginationModelChange = useCallback(
    (model) => {
      if (paginationMode === "server" && loading) return;
      setPaginationModel((prev) => {
        if (paginationMode === "server" && prev.pageSize !== model.pageSize)
          return { ...model, page: 0 };
        return model;
      });
      handlePaginationModelChange(model);
    },
    [handlePaginationModelChange, loading, paginationMode],
  );

  const onColumnVisibilityModelChange = useCallback(
    (model) => {
      if (datagridKey) {
        datagridStore.set(datagridKey)({ columnVisibilityModel: model });
      }
      setColumnVisibilityModel(model);
    },
    [datagridKey, datagridStore, loading, columnVisibilityModel],
  );

  const onSortModelChange = useCallback(
    (model) => {
      if (sortingMode === "server" && loading) return;
      if (datagridKey) {
        datagridStore.set(datagridKey)({ sortModel: model });
        if (sortingMode === "server")
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
      setSortModel(model);
      handleSortModelChange(model);
    },
    [datagridKey, datagridStore, handleSortModelChange, loading, sortingMode],
  );

  const getActions = useCallback(
    (params) =>
      actions
        .filter(({ filter }) => (filter ? filter(params.row) : true))
        .map(
          ({
            disabled,
            label,
            onClick: handleClick,
            icon,
            showInMenu,
            ...menuItemProps
          }) => (
            <GridActionsCellItem
              dense
              disabled={disabled && params?.row && disabled(params.row)}
              key={label}
              label={label}
              icon={icon && params?.row && icon(params.row)}
              onClick={handleClick(params.row)}
              showInMenu={
                typeof showInMenu === "undefined" || showInMenu
                  ? true
                  : undefined
              }
              {...menuItemProps}
            />
          ),
        ),
    [actions],
  );

  const dataGridColumns = useMemo(() => {
    const actionsColumn = actions.length
      ? {
          field: "actions",
          getActions,
          type: "actions",
        }
      : null;
    return [...columns, actionsColumn].filter((column) => column);
  }, [actions, columns, getActions]);

  const dataGridProps = useMemo(
    () => ({
      autoHeight: false,
      autoPageSize: false,
      checkboxSelection,
      columnBuffer: dataGridColumns.length,
      columns: dataGridColumns,
      columnVisibilityModel,
      density: "compact",
      disableSelectionOnClick: true,
      disableVirtualization: typeof jest !== "undefined",
      error,
      filterMode,
      filterModel,
      loading,
      onColumnVisibilityModelChange,
      onFilterModelChange,
      onPaginationModelChange,
      onRowSelectionModelChange: handleSelectionModelChange,
      onSortModelChange,
      pagination: true,
      paginationMode,
      paginationModel,
      rowCount,
      rows,
      sortingMode,
      sortingOrder: ["asc", "desc"],
      sortModel,
    }),
    [
      checkboxSelection,
      dataGridColumns,
      error,
      filterMode,
      filterModel,
      handleSelectionModelChange,
      loading,
      onFilterModelChange,
      onPaginationModelChange,
      onSortModelChange,
      paginationMode,
      paginationModel,
      rowCount,
      rows,
      sortingMode,
      sortModel,
    ],
  );

  const customToolbar = () => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", justifyContent: "flex-start", flex: 1 }}>
          <GridToolbarExport />
        </div>
      </div>
    );
  };

  return (
    <>
      {error ? (
        <Typography align="center" color="error" variant="h5">
          {error}
        </Typography>
      ) : (
        <MuiDataGrid
          {...dataGridProps}
          {...props}
          slots={{
            toolbar: customToolbar,
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {noRowsMessage}
              </Stack>
            ),
          }}
          sx={{
            "& .MuiDataGrid-cell": { lineHeight: "1.2em" },
            "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer":
              {
                display: disableSelectAllCheckbox ? "none" : "flex",
              },
            ...sx,
          }}
        />
      )}
    </>
  );
};

export { DataGrid };
